import { useEffect, useRef, useState } from 'react';
import { Card, Button, Badge } from '@/components/ui/primitives';
import {
  getVoiceSettings,
  saveVoicePace,
  previewVoice,
} from '@/data/voiceSettings';
import { AuraMicModeControl } from '@/voice/wake/AuraMicModeControl';
import {
  PACE_MIN,
  PACE_MAX,
  PACE_STEP,
  PACE_DEFAULT,
  roundPace,
  paceLabel,
  paceDisplay,
} from '@/data/voicePace';

/* Settings › Aura Voice — adjust how fast Aura speaks, at runtime, per workspace.
 * The pace is SERVER-AUTHORITATIVE: the slider PUTs a persisted setting that the
 * TTS proxy reads on every synth (no redeploy needed). Structured as an extensible
 * voice-personality panel — future traits (tone, humour, …) slot into the reserved
 * section below. Follows the ChangePasswordPanel token-driven, minimal pattern. */

const SAVE_DEBOUNCE_MS = 500;
const PREVIEW_TEXT = 'Hello, I am Aura. This is how I sound at this speaking pace.';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export function AuraVoicePanel() {
  const [pace, setPace] = useState<number>(PACE_DEFAULT);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [save, setSave] = useState<SaveState>('idle');
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  // Load the persisted pace on mount.
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    getVoiceSettings(controller.signal)
      .then((s) => {
        if (alive) {
          setPace(roundPace(s.pace));
          setLoading(false);
        }
      })
      .catch(() => {
        if (alive) {
          setLoadError(true);
          setLoading(false);
        }
      });
    return () => {
      alive = false;
      controller.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  function persist(next: number) {
    setSave('saving');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      saveVoicePace(next)
        .then((s) => setSave(s ? 'saved' : 'idle'))
        .catch(() => setSave('error'));
    }, SAVE_DEBOUNCE_MS);
  }

  function onSlide(raw: number) {
    const next = roundPace(raw);
    setPace(next);
    persist(next);
  }

  async function onPreview() {
    setPreviewError(null);
    setPreviewing(true);
    try {
      // Flush the current pace first so the SERVER-AUTHORITATIVE synth reflects it.
      await saveVoicePace(pace);
      setSave('saved');
      const blob = await previewVoice(PREVIEW_TEXT, pace);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      const audio = audioRef.current ?? new Audio();
      audioRef.current = audio;
      audio.src = url;
      await audio.play();
    } catch {
      setPreviewError('Could not play a preview. Voice may be unavailable on this environment.');
    } finally {
      setPreviewing(false);
    }
  }

  return (
    <Card className="max-w-md p-5">
      <div className="mb-1 flex items-center gap-2">
        <h3 className="text-sm font-medium text-text-primary">Aura voice</h3>
        <Badge tone="ai">Beta</Badge>
      </div>
      <p className="mb-4 text-xs text-text-tertiary">
        Adjust how fast Aura speaks. Changes apply to spoken replies right away — no redeploy,
        saved for this workspace.
      </p>

      {/* Microphone mode — ACCOUNT-level (follows the human across workspaces).
          Self-service: any member may set their OWN mode. Separate from pace. */}
      <div className="mb-5 border-b border-border-subtle pb-5">
        <AuraMicModeControl variant="settings" />
      </div>

      {loading ? (
        <p className="text-sm text-text-tertiary">Loading…</p>
      ) : (
        <>
          <div className="mb-1 flex items-baseline justify-between">
            <label htmlFor="aura-pace" className="text-xs font-medium text-text-secondary">
              Speaking pace
            </label>
            <span className="text-xs tabular-nums text-text-secondary">
              {paceLabel(pace)} · <span className="text-text-primary">{paceDisplay(pace)}</span>
            </span>
          </div>
          <input
            id="aura-pace"
            type="range"
            min={PACE_MIN}
            max={PACE_MAX}
            step={PACE_STEP}
            value={pace}
            onChange={(e) => onSlide(Number(e.target.value))}
            aria-label="Aura speaking pace"
            aria-valuetext={paceDisplay(pace)}
            className="h-2 w-full cursor-pointer appearance-none rounded-full bg-inset accent-ai outline-none focus-visible:ring-2 focus-visible:ring-ai/40"
          />
          <div className="mt-1 flex justify-between text-[11px] text-text-tertiary">
            <span>Slower</span>
            <span>Faster</span>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <Button variant="secondary" onClick={onPreview} disabled={previewing}>
              {previewing ? 'Playing…' : '▶ Preview'}
            </Button>
            <span className="text-xs text-text-tertiary" role="status" aria-live="polite">
              {save === 'saving' && 'Saving…'}
              {save === 'saved' && 'Saved'}
              {save === 'error' && <span className="text-danger">Couldn’t save</span>}
            </span>
          </div>

          {loadError && (
            <p className="mt-3 text-xs text-danger">
              Couldn’t load the saved pace; showing the default. Saving still works.
            </p>
          )}
          {previewError && <p className="mt-3 text-xs text-danger">{previewError}</p>}
        </>
      )}

      {/* Extensible voice-personality structure — future traits slot in here. */}
      <div className="mt-6 border-t border-border-subtle pt-4">
        <div className="mb-1 flex items-center gap-2">
          <span className="text-xs font-medium text-text-secondary">Voice personality</span>
          <Badge tone="neutral">Coming soon</Badge>
        </div>
        <p className="text-[11px] text-text-tertiary">
          More voice personality options are coming soon — tone, humour, warmth. These will slot in
          alongside pace, saved per workspace.
        </p>
        <div className="mt-3 space-y-3 opacity-50" aria-hidden>
          {['Tone', 'Humour', 'Warmth'].map((t) => (
            <div key={t}>
              <div className="mb-1 text-[11px] text-text-tertiary">{t}</div>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                defaultValue={0.5}
                disabled
                tabIndex={-1}
                className="h-2 w-full appearance-none rounded-full bg-inset accent-ai"
              />
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
